import React, {ReactNode} from 'react';

import {TimeFormatsEnum} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../libs/shared/core/src/models/api/inboxModel';
import {
  CellsContext,
  CellsContextTypesEnum,
} from '../../../../libs/shared/core/src/store/panels/cells/context/cellsContext';
import {AbsoluteDateTimeFormatter} from '../../../../libs/shared/ui/src/components/datetime/absoluteDateTimeFormatter';
import {
  computeIsAnyConversationDraftLater,
  considerInPrivateTag,
  isConversationInTeamContextOrSelfAssigned,
} from '../../../helpers/models/conversationHelpers';
import {ConversationBaseModel} from '../../../models/api/conversationModel';
import {ReminderModel} from '../../../models/api/reminderModel';
import {
  hasSearchInInboxesParameters,
  InboxesParameters,
  InboxesParametersTypesEnum,
  InboxesSelectionTypesEnum,
} from '../../../store/panels/inboxes/parameters/inboxesParameters';
import {ConversationsArchiveButtonProps} from '../viewer/conversation/actions/archive/conversationsArchiveCoordinator';
import {ConversationsTrashButtonProps} from '../viewer/conversation/actions/trash/conversationsTrashCoordinator';

interface InboxesTopbarTextCommonProps {
  cellsContext: CellsContext;
  conversationsInboxes: ReadonlyArray<InboxModel>;
  conversations: ReadonlyArray<ConversationBaseModel>;
  inboxesParameters: InboxesParameters;
}

export interface InboxesTopbarTooltipProps {
  mainButton: ReactNode;
  firstLine?: ReactNode;
  secondLine: ReactNode;
}

const archiveNormalSecondLine = (
  <Trans id="archive-normal-second-line">
    <ul>
      <li>Will reopen for all new activity</li>
      <li>Doesn&apos;t archive for subscribers</li>
    </ul>
  </Trans>
);
const archivedSecondLinePersonal = (
  <Trans id="archived-second-line-personal">
    <ul>
      <li>Will reopen in your inbox</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);
const archivedSecondLineTeamInbox = (
  <Trans id="archived-second-line-team-inbox">
    <ul>
      <li>Will reopen in your shared inbox</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);
const archivedSecondLineTeamInboxes = (
  <Trans id="archived-second-line-team-inboxes">
    <ul>
      <li>Will reopen in your shared inboxes</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);

export function makeInboxesTopbarArchiveTooltipProps(
  buttonProps: ConversationsArchiveButtonProps,
  props: InboxesTopbarTextCommonProps,
): InboxesTopbarTooltipProps {
  const {areAllArchived} = buttonProps;
  const {cellsContext, conversationsInboxes, conversations, inboxesParameters} = props;
  const inSelectAllAndTeamContext = isInSelectAllAndTeamContext(cellsContext, inboxesParameters);

  const areAllInTeamContext = conversations.every((c) =>
    isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const areAllInTag = conversations.every((c) => considerInPrivateTag(c, cellsContext, inboxesParameters));
  const areSomeInTeamContext = conversations.some((c) =>
    isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const areSomeInNonTeamContext = conversations.some(
    (c) => !isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const isInMixedContext = areSomeInTeamContext && areSomeInNonTeamContext;

  if (inSelectAllAndTeamContext) {
    if (areAllArchived) {
      const mainButton = <Trans id="conversation-actions-reopen">Reopen</Trans>;
      return {mainButton, secondLine: archivedSecondLinePersonal};
    }

    const mainButton = <Trans id="conversation-actions-archive">Archive</Trans>;
    return {mainButton, secondLine: archiveNormalSecondLine};
  }

  const isInSearch = considerInSearch(inboxesParameters);
  const actionOnlyContent = areAllArchived ? (
    <Trans id="conversation-actions-reopen">Reopen</Trans>
  ) : (
    <Trans id="conversation-actions-archive">Archive</Trans>
  );
  const maybeRenderFirstLine = (content: ReactNode) => (isInSearch ? content : undefined);
  const renderMainButton = (content: ReactNode) => (isInSearch ? actionOnlyContent : content);

  if (areAllInTag) {
    if (areAllArchived) {
      const mainButton = <Trans id="conversation-actions-reopeninmytag">Reopen in my tag</Trans>;
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: archivedSecondLinePersonal,
      };
    }

    const mainButton = <Trans id="conversation-actions-archiveinmytag">Archive in my tag</Trans>;
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: archiveNormalSecondLine,
    };
  }

  if (areAllInTeamContext) {
    if (conversationsInboxes.length === 1) {
      if (areAllArchived) {
        const mainButton = (
          <Trans id="conversation-actions-reopenininbox">Reopen in {conversationsInboxes[0].name}</Trans>
        );
        return {
          mainButton: renderMainButton(mainButton),
          firstLine: maybeRenderFirstLine(mainButton),
          secondLine: archivedSecondLineTeamInbox,
        };
      }

      const mainButton = (
        <Trans id="conversation-actions-archiveininbox">Archive in {conversationsInboxes[0].name}</Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: archiveNormalSecondLine,
      };
    }

    if (areAllArchived) {
      const mainButton = (
        <Trans id="conversation-actions-reopeninmanyinboxes">
          Reopen in {conversationsInboxes.length} inboxes
        </Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: archivedSecondLineTeamInboxes,
      };
    }

    const mainButton = (
      <Trans id="conversation-actions-archiveinmanyinboxes">
        Archive in {conversationsInboxes.length} inboxes
      </Trans>
    );
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: archiveNormalSecondLine,
    };
  }

  if (cellsContext.isDelegation) {
    if (areAllArchived) {
      const mainButton = <Trans id="conversation-actions-reopeninteammate">Reopen in teammate inbox</Trans>;
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: archivedSecondLinePersonal,
      };
    }

    const mainButton = <Trans id="conversation-actions-archiveinteammate">Archive in teammate inbox</Trans>;
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: archiveNormalSecondLine,
    };
  }

  if (areAllArchived) {
    if (isInMixedContext) {
      const mainButton = (
        <Trans id="conversation-actions-reopeninmanyinboxes">
          Reopen in {conversationsInboxes.length} inboxes
        </Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: archivedSecondLinePersonal,
      };
    }

    const mainButton = <Trans id="conversation-actions-reopeninmyinbox">Reopen in my inbox</Trans>;
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: archivedSecondLinePersonal,
    };
  }

  if (isInMixedContext) {
    const mainButton = (
      <Trans id="conversation-actions-archiveinmanyinboxes">
        Archive in {conversationsInboxes.length} inboxes
      </Trans>
    );
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: archiveNormalSecondLine,
    };
  }

  const mainButton = <Trans id="conversation-actions-archiveinmyinbox">Archive in my inbox</Trans>;
  return {
    mainButton: renderMainButton(mainButton),
    firstLine: maybeRenderFirstLine(mainButton),
    secondLine: archiveNormalSecondLine,
  };
}

const trashedSecondLinePersonal = (
  <Trans id="trashed-second-line-personal">
    <ul>
      <li>Will reopen in your inbox</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);
const trashedSecondLineTeamInbox = (
  <Trans id="trashed-second-line-team-inbox">
    <ul>
      <li>Will reopen in your shared inbox</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);
const trashedSecondLineTeamInboxes = (
  <Trans id="trashed-second-line-team-inboxes">
    <ul>
      <li>Will reopen in your shared inboxes</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);
const trashNormalSecondLine = (
  <Trans id="trash-normal-second-line">
    <ul>
      <li>Won&apos;t appear in search results</li>
      <li>Doesn&apos;t trash for subscribers</li>
    </ul>
  </Trans>
);

export function makeInboxesTopbarTrashTooltipProps(
  buttonProps: ConversationsTrashButtonProps,
  props: InboxesTopbarTextCommonProps,
): InboxesTopbarTooltipProps {
  const {areAllTrashed} = buttonProps;
  const {cellsContext, conversationsInboxes, conversations, inboxesParameters} = props;
  const inSelectAllAndTeamContext = isInSelectAllAndTeamContext(cellsContext, inboxesParameters);

  const areAllInTeamContext = conversations.every((c) =>
    isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const areAllInTag = conversations.every((c) => considerInPrivateTag(c, cellsContext, inboxesParameters));
  const areSomeInTeamContext = conversations.some((c) =>
    isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const areSomeInNonTeamContext = conversations.some(
    (c) => !isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const isInMixedContext = areSomeInTeamContext && areSomeInNonTeamContext;

  const isAnyConversationDraftLater = computeIsAnyConversationDraftLater(props.conversations);

  if (inSelectAllAndTeamContext) {
    if (isAnyConversationDraftLater) {
      return {
        mainButton: <Trans id="conversation-actions-trash">Trash</Trans>,
        secondLine: (
          <Trans id="conversaiton-actions-trash-draft-later-all">
            You have selected a draft that will send later, cancel it or deselect it.
          </Trans>
        ),
      };
    }

    if (areAllTrashed) {
      return {
        mainButton: <Trans id="conversation-actions-restore">Restore</Trans>,
        secondLine: trashedSecondLinePersonal,
      };
    }

    return {
      mainButton: <Trans id="conversation-actions-trash">Trash</Trans>,
      secondLine: trashNormalSecondLine,
    };
  }

  if (areAllInTag) {
    if (areAllTrashed) {
      return {
        mainButton: <Trans id="conversation-actions-restoreinmytag">Restore in my tag</Trans>,
        secondLine: trashedSecondLinePersonal,
      };
    }

    return {
      mainButton: <Trans id="conversation-actions-trashinmytag">Trash in my tag</Trans>,
      secondLine: trashNormalSecondLine,
    };
  }

  const isInSearch = considerInSearch(inboxesParameters);
  const actionOnlyContent = areAllTrashed ? (
    <Trans id="conversation-actions-restore">Restore</Trans>
  ) : (
    <Trans id="conversation-actions-trash">Trash</Trans>
  );
  const maybeRenderFirstLine = (content: ReactNode) => (isInSearch ? content : undefined);
  const renderMainButton = (content: ReactNode) => (isInSearch ? actionOnlyContent : content);

  if (areAllInTeamContext) {
    if (conversationsInboxes.length === 1) {
      if (areAllTrashed) {
        const mainButton = (
          <Trans id="conversation-actions-restoreininbox">Restore in {conversationsInboxes[0].name}</Trans>
        );
        return {
          mainButton: renderMainButton(mainButton),
          firstLine: maybeRenderFirstLine(mainButton),
          secondLine: trashedSecondLineTeamInbox,
        };
      }

      const mainButton = (
        <Trans id="conversation-actions-trashininbox">Trash in {conversationsInboxes[0].name}</Trans>
      );

      if (isAnyConversationDraftLater) {
        return {
          mainButton: renderMainButton(mainButton),
          secondLine: (
            <Trans id="conversaiton-actions-trash-draft-later">
              You have selected a draft that will send later, you must cancel it before you can delete it.
            </Trans>
          ),
        };
      }

      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: trashNormalSecondLine,
      };
    }

    if (areAllTrashed) {
      const mainButton = (
        <Trans id="conversation-actions-restoreinmanyinboxes">
          Restore in {conversationsInboxes.length} inboxes
        </Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: trashedSecondLineTeamInboxes,
      };
    }

    const mainButton = (
      <Trans id="conversation-actions-trashinmanyinboxes">
        Trash in {conversationsInboxes.length} inboxes
      </Trans>
    );
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: trashNormalSecondLine,
    };
  }

  if (cellsContext.isDelegation) {
    if (areAllTrashed) {
      const mainButton = <Trans id="conversation-actions-restoreinteammate">Restore in teammate inbox</Trans>;
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: trashedSecondLinePersonal,
      };
    }

    const mainButton = <Trans id="conversation-actions-trashinteammate">Trash in teammate inbox</Trans>;
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: trashNormalSecondLine,
    };
  }

  if (areAllTrashed) {
    if (isInMixedContext) {
      const mainButton = (
        <Trans id="conversation-actions-restoreinmany">
          Restore in {conversationsInboxes.length} inboxes
        </Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: trashedSecondLinePersonal,
      };
    }

    const mainButton = <Trans id="conversation-actions-restoreinmyinbox">Restore in my inbox</Trans>;
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: trashedSecondLinePersonal,
    };
  }

  if (isInMixedContext) {
    const mainButton = (
      <Trans id="conversation-actions-trashinmany">Trash in {conversationsInboxes.length} inboxes</Trans>
    );
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: trashNormalSecondLine,
    };
  }

  const mainButton = <Trans id="conversation-actions-trashinmyinbox">Trash in my inbox</Trans>;
  return {
    mainButton: renderMainButton(mainButton),
    firstLine: maybeRenderFirstLine(mainButton),
    secondLine: trashNormalSecondLine,
  };
}

const spammedSecondLinePersonal = (
  <Trans id="spammed-second-line-personal">
    <ul>
      <li>Will reopen in your inbox</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);
const spammedSecondLineTeamInbox = (
  <Trans id="spammed-second-line-team-inbox">
    <ul>
      <li>Will reopen in your shared inbox</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);
const spammedSecondLineTeamInboxes = (
  <Trans id="spammed-second-line-team-inboxes">
    <ul>
      <li>Will reopen in your shared inboxes</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);
const spammedNormalSecondLine = (
  <Trans id="spammed-normal-second-line">
    <ul>
      <li>Won&apos;t appear in search results</li>
      <li>Doesn&apos;t mark as spam for subscribers</li>
    </ul>
  </Trans>
);

export function makeInboxesTopbarSpamTooltipProps(
  buttonProps: ConversationsTrashButtonProps,
  props: InboxesTopbarTextCommonProps,
): InboxesTopbarTooltipProps {
  const {areAllSpam} = buttonProps;
  const {cellsContext, conversationsInboxes, conversations, inboxesParameters} = props;
  const inSelectAllAndTeamContext = isInSelectAllAndTeamContext(cellsContext, inboxesParameters);

  const areAllInTeamContext = conversations.every((c) =>
    isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const areAllInTag = conversations.every((c) => considerInPrivateTag(c, cellsContext, inboxesParameters));
  const areSomeInTeamContext = conversations.some((c) =>
    isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const areSomeInNonTeamContext = conversations.some(
    (c) => !isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const isInMixedContext = areSomeInTeamContext && areSomeInNonTeamContext;

  if (inSelectAllAndTeamContext) {
    if (areAllSpam) {
      return {
        mainButton: <Trans id="conversation-actions-restore">Restore</Trans>,
        secondLine: spammedSecondLinePersonal,
      };
    }

    return {
      mainButton: <Trans id="conversation-actions-markasspam">Mark as spam</Trans>,
      secondLine: spammedNormalSecondLine,
    };
  }

  const isInSearch = considerInSearch(inboxesParameters);
  const actionOnlyContent = areAllSpam ? (
    <Trans id="conversation-actions-restore">Restore</Trans>
  ) : (
    <Trans id="conversation-actions-markasspam">Mark as spam</Trans>
  );
  const maybeRenderFirstLine = (content: ReactNode) => (isInSearch ? content : undefined);
  const renderMainButton = (content: ReactNode) => (isInSearch ? actionOnlyContent : content);

  if (areAllInTag) {
    if (areAllSpam) {
      const mainButton = <Trans id="conversation-actions-restoreinmytag">Restore in my tag</Trans>;
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: spammedSecondLinePersonal,
      };
    }

    const mainButton = <Trans id="conversation-actions-markasspaminmytag">Mark as spam in my tag</Trans>;
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: spammedNormalSecondLine,
    };
  }

  if (areAllInTeamContext) {
    if (conversationsInboxes.length === 1) {
      if (areAllSpam) {
        const mainButton = (
          <Trans id="conversation-actions-restoreininbox">Restore in {conversationsInboxes[0].name}</Trans>
        );
        return {
          mainButton: renderMainButton(mainButton),
          firstLine: maybeRenderFirstLine(mainButton),
          secondLine: spammedSecondLineTeamInbox,
        };
      }

      const mainButton = (
        <Trans id="conversation-actions-markasspamininbox">
          Mark as spam in {conversationsInboxes[0].name}
        </Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: spammedNormalSecondLine,
      };
    }

    if (areAllSpam) {
      const mainButton = (
        <Trans id="conversation-actions-restoreinmanyinboxes">
          Restore in {conversationsInboxes.length} inboxes
        </Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: spammedSecondLineTeamInboxes,
      };
    }

    const mainButton = (
      <Trans id="conversation-actions-markasspaminmanyinboxes">
        Mark as spam in {conversationsInboxes.length} inboxes
      </Trans>
    );
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: spammedNormalSecondLine,
    };
  }

  if (cellsContext.isDelegation) {
    if (areAllSpam) {
      const mainButton = <Trans id="conversation-actions-restoreinteammate">Restore in teammate inbox</Trans>;
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: spammedSecondLinePersonal,
      };
    }

    const mainButton = (
      <Trans id="conversation-actions-markasspaminteammate">Mark as spam in teammate inbox</Trans>
    );
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: spammedNormalSecondLine,
    };
  }

  if (areAllSpam) {
    if (isInMixedContext) {
      const mainButton = (
        <Trans id="conversation-actions-restoreinmanyinboxes">
          Restore in {conversationsInboxes.length} inboxes
        </Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: spammedSecondLinePersonal,
      };
    }

    const mainButton = <Trans id="conversation-actions-restoreinmyinbox">Restore in my inbox</Trans>;
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: spammedSecondLinePersonal,
    };
  }

  if (isInMixedContext) {
    const mainButton = (
      <Trans id="conversation-actions-restoreinmanyinboxes">
        Restore in {conversationsInboxes.length} inboxes
      </Trans>
    );
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: spammedNormalSecondLine,
    };
  }
  const mainButton = <Trans id="conversation-actions-markasspaminmyinbox">Mark as spam in my inbox</Trans>;

  return {
    mainButton: renderMainButton(mainButton),
    firstLine: maybeRenderFirstLine(mainButton),
    secondLine: spammedNormalSecondLine,
  };
}

const snoozedSecondLinePersonal = (
  <Trans id="snoozed-second-line-personal">
    <ul>
      <li>Will unsnooze and reopen in your inbox</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);
const snoozedSecondLineTeamInbox = (
  <Trans id="snoozed-second-line-team-inbox">
    <ul>
      <li>Will unsnooze and reopen in your shared inbox</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);
const snoozedSecondLineTeamInboxes = (
  <Trans id="snoozed-second-line-team-inboxes">
    <ul>
      <li>Will unsooze and reopen at a specific time if no new activity</li>
      <li>Doesn&apos;t reopen for subscribers</li>
    </ul>
  </Trans>
);
const snoozeSecondLine = (
  <Trans id="snooze-second-line">
    <ul>
      <li>Will reopen at a specific time or for new activity</li>
      <li>Doesn&apos;t snooze for subscribers</li>
    </ul>
  </Trans>
);

export function makeInboxesTopbarSnoozeTooltipProps(
  props: InboxesTopbarTextCommonProps & {
    remindersForAll: ReadonlyArray<ReminderModel>;
    timeFormat: TimeFormatsEnum;
  },
): InboxesTopbarTooltipProps {
  const {remindersForAll, timeFormat} = props;
  const {cellsContext, conversationsInboxes, conversations, inboxesParameters} = props;
  const inSelectAllAndTeamContext = isInSelectAllAndTeamContext(cellsContext, inboxesParameters);

  const areAllInTeamContext = conversations.every((c) =>
    isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const areAllInTag = conversations.every((c) => considerInPrivateTag(c, cellsContext, inboxesParameters));
  const areSomeInTeamContext = conversations.some((c) =>
    isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const areSomeInNonTeamContext = conversations.some(
    (c) => !isConversationInTeamContextOrSelfAssigned(c, cellsContext),
  );
  const isInMixedContext = areSomeInTeamContext && areSomeInNonTeamContext;

  if (inSelectAllAndTeamContext) {
    if (remindersForAll.length) {
      if (remindersForAll.length > 1) {
        return {
          mainButton: <Trans id="conversation-actions-snoozed">Snoozed</Trans>,
          secondLine: snoozedSecondLinePersonal,
        };
      }

      return {
        mainButton: (
          <Trans id="conversation-actions-snoozeduntil">
            Snoozed until{' '}
            <AbsoluteDateTimeFormatter value={remindersForAll[0].remindAt} timeFormat={timeFormat} />
          </Trans>
        ),
        secondLine: snoozedSecondLinePersonal,
      };
    }

    return {mainButton: <Trans id="conversation-actions-snooze">Snooze</Trans>, secondLine: snoozeSecondLine};
  }

  const isInSearch = considerInSearch(inboxesParameters);
  const actionOnlyContent = remindersForAll.length ? (
    <Trans id="conversation-actions-unsnooze">Unsnooze</Trans>
  ) : (
    <Trans id="conversation-actions-snooze">Snooze</Trans>
  );
  const maybeRenderFirstLine = (content: ReactNode) => (isInSearch ? content : undefined);
  const renderMainButton = (content: ReactNode) => (isInSearch ? actionOnlyContent : content);

  if (areAllInTag) {
    if (remindersForAll.length) {
      if (remindersForAll.length > 1) {
        const mainButton = <Trans id="conversation-actions-snoozedinmytag">Snoozed in my tag</Trans>;
        return {
          mainButton: renderMainButton(mainButton),
          firstLine: maybeRenderFirstLine(mainButton),
          secondLine: snoozedSecondLinePersonal,
        };
      }

      const mainButton = (
        <Trans id="conversation-actions-snoozedinmytaguntil">
          Snoozed in my tag until{' '}
          <AbsoluteDateTimeFormatter value={remindersForAll[0].remindAt} timeFormat={timeFormat} />
        </Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: snoozedSecondLinePersonal,
      };
    }
    const mainButton = <Trans id="conversation-actions-snoozeinmytag">Snooze in my tag</Trans>;

    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: snoozeSecondLine,
    };
  }

  if (areAllInTeamContext) {
    if (conversationsInboxes.length === 1) {
      if (remindersForAll.length) {
        if (remindersForAll.length > 1) {
          const mainButton = (
            <Trans id="conversation-actions-snoozedininbox">Snoozed in {conversationsInboxes[0].name}</Trans>
          );
          return {
            mainButton: renderMainButton(mainButton),
            firstLine: maybeRenderFirstLine(mainButton),
            secondLine: snoozedSecondLineTeamInbox,
          };
        }

        const mainButton = (
          <Trans id="conversation-actions-snoozedininboxuntil">
            Snoozed in {conversationsInboxes[0].name} until{' '}
            <AbsoluteDateTimeFormatter value={remindersForAll[0].remindAt} timeFormat={timeFormat} />
          </Trans>
        );
        return {
          mainButton: renderMainButton(mainButton),
          firstLine: maybeRenderFirstLine(mainButton),
          secondLine: snoozedSecondLineTeamInbox,
        };
      }

      const mainButton = (
        <Trans id="conversation-actions-snoozeininbox">Snooze in {conversationsInboxes[0].name}</Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: snoozeSecondLine,
      };
    }

    if (remindersForAll.length) {
      if (remindersForAll.length > 1) {
        const mainButton = (
          <Trans id="conversation-actions-snoozedinmanyinboxes">
            Snoozed in {conversationsInboxes.length} inboxes
          </Trans>
        );
        return {
          mainButton: renderMainButton(mainButton),
          firstLine: maybeRenderFirstLine(mainButton),
          secondLine: snoozedSecondLineTeamInboxes,
        };
      }
      const mainButton = (
        <Trans id="conversation-actions-snoozedinmanyinboxesuntil">
          Snoozed in {conversationsInboxes.length} inboxes until{' '}
          <AbsoluteDateTimeFormatter value={remindersForAll[0].remindAt} timeFormat={timeFormat} />
        </Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: snoozedSecondLineTeamInboxes,
      };
    }

    const mainButton = (
      <Trans id="conversation-actions-snoozeinmanyinboxes">
        Snooze in {conversationsInboxes.length} inboxes
      </Trans>
    );
    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: snoozeSecondLine,
    };
  }

  if (cellsContext.isDelegation) {
    if (remindersForAll.length) {
      if (remindersForAll.length > 1) {
        const mainButton = (
          <Trans id="conversation-actions-snoozedinteammate">Snoozed in teammate inbox</Trans>
        );
        return {
          mainButton: renderMainButton(mainButton),
          firstLine: maybeRenderFirstLine(mainButton),
          secondLine: snoozedSecondLinePersonal,
        };
      }

      const mainButton = (
        <Trans id="conversation-actions-snoozedinteammateuntil">
          Snoozed in teammate inbox until{' '}
          <AbsoluteDateTimeFormatter value={remindersForAll[0].remindAt} timeFormat={timeFormat} />
        </Trans>
      );
      return {
        mainButton: renderMainButton(mainButton),
        firstLine: maybeRenderFirstLine(mainButton),
        secondLine: snoozedSecondLinePersonal,
      };
    }
    const mainButton = <Trans id="conversation-actions-snoozeinteammate">Snooze in teammate inbox</Trans>;

    return {
      mainButton: renderMainButton(mainButton),
      firstLine: maybeRenderFirstLine(mainButton),
      secondLine: snoozeSecondLine,
    };
  }

  if (remindersForAll.length) {
    if (remindersForAll.length > 1) {
      if (isInMixedContext) {
        return {
          mainButton: (
            <Trans id="conversation-actions-snoozedinmanyinboxes">
              Snoozed in {conversationsInboxes.length} inboxes
            </Trans>
          ),
          secondLine: snoozedSecondLinePersonal,
        };
      }

      return {
        mainButton: <Trans id="conversation-actions-snoozedinmyinbox">Snoozed in my inbox</Trans>,
        secondLine: snoozedSecondLinePersonal,
      };
    }

    return {
      mainButton: (
        <Trans id="conversation-actions-snoozedinmyinboxuntil">
          Snoozed in my inbox until{' '}
          <AbsoluteDateTimeFormatter value={remindersForAll[0].remindAt} timeFormat={timeFormat} />
        </Trans>
      ),
      secondLine: snoozedSecondLinePersonal,
    };
  }

  if (isInMixedContext) {
    return {
      mainButton: (
        <Trans id="conversation-actions-snoozeinmanyinboxes">
          Snooze in {conversationsInboxes.length} inboxes
        </Trans>
      ),
      secondLine: snoozeSecondLine,
    };
  }

  return {
    mainButton: <Trans id="conversation-actions-snoozeinmyinbox">Snooze in my inbox</Trans>,
    secondLine: snoozeSecondLine,
  };
}

/*
 * Helpers.
 */

function isInSelectAllAndTeamContext(cellsContext: CellsContext, inboxesParameters: InboxesParameters) {
  return (
    inboxesParameters.selection.type === InboxesSelectionTypesEnum.ALL_EXCEPT &&
    (inboxesParameters.type === InboxesParametersTypesEnum.TEAM ||
      cellsContext.type !== CellsContextTypesEnum.TEAMMATE)
  );
}

function considerInSearch(parameters: InboxesParameters) {
  return hasSearchInInboxesParameters(parameters) && parameters.search.query.length > 0;
}
